/* eslint-disable react/prop-types */
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Popper,
  TextField,
  PopperProps,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import { CSSProperties, SyntheticEvent, ReactNode, RefObject } from 'react';
import { SxProps } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import type { Classification } from '@/types/classification';

export interface Option {
  label: string;
  value: string | number | Classification | undefined;
  disabled?: boolean;
}

type AutocompleteStyledProps = {
  id?: string;
  options: Option[];
  defaultValue?: Option | null;
  inputValue?: string;
  onInputChange?: (event: SyntheticEvent, value: string) => void;
  isOptionEqualToValue?: (option: Option, value: Option) => boolean;
  value?: Option | null;
  onChange: (event: SyntheticEvent, value: Option | null) => void;
  fullWidth?: boolean;
  sx?: SxProps;
  style?: CSSProperties;
  helperText?: string;
  name: string;
  label?: string;
  startIcon?: ReactNode;
  margin?: 'normal' | 'dense' | 'none';
  type?:
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week';
  required?: boolean;
  redDot?: boolean;
  getOptionLabel?: (option: Option) => string;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  disableClearable?: boolean;
  ref?: RefObject<HTMLDivElement | null>;
  className?: string;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & { key: string },
    option: Option
  ) => ReactNode;
};

export const AutocompleteStyled = ({
  id,
  className,
  options,
  defaultValue,
  inputValue,
  onInputChange,
  isOptionEqualToValue,
  value,
  onChange,
  fullWidth = true,
  sx,
  style,
  helperText,
  name = '',
  label,
  margin = 'normal',
  type = 'text',
  required = false,
  redDot = false,
  startIcon,
  getOptionLabel = (option: Option) => option.label,
  loading,
  disabled,
  placeholder,
  disableClearable,
  ref,
  open,
  onOpen,
  onClose,
  renderOption,
}: AutocompleteStyledProps) => {
  const theme = useTheme();

  const CustomPopper = (props: PopperProps) => (
    <Popper {...props} style={{ ...props.style, zIndex: 9999 }} />
  );

  const generateStartAdornment = () => {
    if (startIcon) {
      return <InputAdornment position="start">{startIcon}</InputAdornment>;
    }
    return null;
  };

  return (
    <Autocomplete
      id={id}
      className={className}
      ref={ref}
      options={options}
      defaultValue={defaultValue}
      inputValue={inputValue}
      onInputChange={onInputChange}
      PopperComponent={CustomPopper}
      isOptionEqualToValue={
        isOptionEqualToValue || ((option, value) => option.value === value.value)
      }
      value={value}
      onChange={onChange}
      fullWidth={fullWidth}
      getOptionLabel={getOptionLabel}
      disabled={disabled}
      loading={loading}
      getOptionDisabled={(option) => option.disabled || false}
      disableClearable={disableClearable}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      sx={{
        '& .MuiAutocomplete-endAdornment': {
          '& .MuiSvgIcon-root': {
            color: theme.palette.action.active,
          },
        },
        '& .MuiCircularProgress-root': {
          color: theme.palette.action.active,
        },
        '& .MuiAutocomplete-option[aria-disabled="true"]': {
          opacity: 0.5,
          pointerEvents: 'none',
        },
        '.MuiOutlinedInput-root': {
          padding: '0 8px',
          borderRadius: '8px',
          border: `1px solid ${theme.palette.action.active}`,
          height: '44px',
        },
        '.MuiAutocomplete-input': {
          color: theme.palette.text.secondary,
        },
        ...sx,
      }}
      style={style}
      renderOption={(props, option) => {
        if (renderOption) {
          return renderOption(
            props as React.HTMLAttributes<HTMLLIElement> & { key: string },
            option
          );
        }

        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key} {...rest}>
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: generateStartAdornment(),
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {redDot && (
                  <InputAdornment position="end">
                    <Circle />
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            backgroundColor: theme.palette.common.white,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
          data-testid={`autocomplete-${name}`}
          error={Boolean(helperText)}
          helperText={helperText}
          name={name}
          label={label}
          margin={margin}
          type={type}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
        />
      )}
    />
  );
};
